/**
 *
 *  Organizations/Show/Settings/FilesUpload
 *
 */

import React, { useCallback } from 'react'
// NPM Libraries
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useDropzone } from 'react-dropzone'

// GraphQL
import { gql, useMutation } from '@apollo/client'

// Components
import DeleteButton from 'components/DeleteButton'
import { COLORS } from 'global-styles'

const FilesContainer = styled.div`
	border: 1px solid ${COLORS.borderGrey};
	border-bottom: 0;
	margin-top: 10px;

	.item {
		display: grid;
		grid-template-columns: 80% 1fr;

		border-bottom: 1px solid ${COLORS.borderGrey};

		.icons {
			display: flex;
			justify-content: flex-end;
			align-items: center;

			svg {
				cursor: pointer;
			}
		}
	}
`

const UPLOAD_ATTACHMENTS = gql`
	mutation uploadAttachments($organization_id: ID, $files: [Upload!]) {
		uploadAttachments(input: { organization_id: $organization_id, files: $files }) {
			attachments {
				id
				name
				url
			}
		}
	}
`

const DELETE_ATTACHMENT = gql`
	mutation deleteAttachment($id: ID) {
		deleteAttachment(input: { id: $id }) {
			attachment {
				id
			}
		}
	}
`

function FilesUpload({ organization: { id, compliance_packages }, refetch }) {
	const [upload, { loading: uploading }] = useMutation(UPLOAD_ATTACHMENTS, { onCompleted: refetch })
	const [remove, { loading: deleting }] = useMutation(DELETE_ATTACHMENT, { onCompleted: refetch })

	const onDrop = useCallback(
		(acceptedFiles) => {
			upload({
				variables: {
					organization_id: id,
					files: acceptedFiles,
				},
			})
		},
		[id],
	)

	const { getRootProps, getInputProps } = useDropzone({
		onDrop,
		multiple: false,
		accept: 'image/*, .pdf, .zip',
	})

	return (
		<div className="card px-4 py-3">
			<div className="d-flex justify-content-between">
				<h4>Compliance Packages</h4>

				<div>
					<input {...getInputProps()} />
					<button className="btn btn-primary" onClick={getRootProps().onClick} disabled={uploading}>
						<span>{uploading ? 'Uploading File' : 'Upload File'}</span>
						<FontAwesomeIcon icon={uploading ? 'spinner' : 'upload'} className="ml-2" spin={uploading} />
					</button>
				</div>
			</div>

			<FilesContainer>
				{compliance_packages?.map((file) => (
					<div key={file.id} className="item py-2 px-3">
						<span>{file.name}</span>

						<div className="icons">
							<a href={`${process.env.API_URL}/api/v1/attachments/${file.id}`} target="_blank">
								<FontAwesomeIcon icon="download" className="text-primary mr-3" />
							</a>

							<DeleteButton onDelete={() => !deleting && remove({ variables: { id: file.id } })} />
						</div>
					</div>
				))}

				{compliance_packages?.length === 0 && (
					<div className="d-flex justify-content-center align-items-center border-bottom p-2">
						No Compliance Packages
					</div>
				)}
			</FilesContainer>
		</div>
	)
}

FilesUpload.propTypes = {
	organization: PropTypes.object,
	refetch: PropTypes.func,
}

export default FilesUpload
